import { Component, Input, AfterViewInit, ViewChild, ElementRef, forwardRef, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import SignaturePad from 'signature_pad';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { AuthService } from '../../services/auth.service';
import { SignatureModalComponent } from './signature/signature-modal.component';
import { SignatureData, SignerInfo } from '../../models/signature.model';

@Component({
  selector: 'app-form-signature',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, SignatureModalComponent],
  template: `
    <div class="form-section" [attr.name]="name">
      <label *ngIf="label" [class.required-field]="required">{{ label }}</label>

      <!-- 簽名顯示區域 -->
      <div class="signature-display-container"
           [style.width.px]="width"
           [style.height.px]="height"
           (click)="openSignatureModal()"
           [class.has-signature]="hasSignature"
           [class.clickable]="!isDisabled">

        <!-- 已有簽名時顯示簽名圖片 -->
        <div *ngIf="hasSignature" class="signature-preview">
          <img [src]="currentSignature?.imageData" alt="Signature" class="signature-image">
          <div class="signature-info" *ngIf="currentSignature?.signerInfo">
            <div class="signer-name">
              <i class="fas fa-user"></i>
              {{ currentSignature?.signerInfo?.username }}
            </div>
            <div class="signed-time">
              <i class="fas fa-clock"></i>
              {{ formatSignedTime(currentSignature?.signerInfo?.signedAt!) }}
            </div>
          </div>
          <div class="signature-overlay" *ngIf="!isDisabled">
            <i class="fas fa-edit"></i>
            <span>{{ 'form.signature.click_to_edit' | translate }}</span>
          </div>
        </div>

        <!-- 沒有簽名時顯示提示 -->
        <div *ngIf="!hasSignature" class="signature-placeholder">
          <i class="fas fa-signature"></i>
          <p>{{ 'form.signature.click_to_sign' | translate }}</p>
        </div>
      </div>

      <!-- 清除簽名按鈕 -->
      <div class="signature-actions" *ngIf="hasSignature && !isDisabled">
        <button type="button"
                class="btn btn-outline-danger btn-sm"
                (click)="clearSignature($event)">
          <i class="fas fa-trash"></i>
          {{ clearButtonText || ('form.signature.clear_button' | translate) }}
        </button>
      </div>
    </div>

    <!-- 全螢幕簽名 Modal -->
    <app-signature-modal
      [initialSignature]="currentSignature"
      (signatureSaved)="onSignatureSaved($event)"
      (modalClosed)="onModalClosed()"
      #signatureModal>
    </app-signature-modal>
  `,
  styles: [`
    .form-section {
      margin-bottom: 15px;
    }

    label {
      display: block;
      margin-bottom: 5px;
      font-weight: bold;
    }

    .required-field::after {
      content: ' *';
      color: red;
    }

    .signature-display-container {
      border: 2px solid #ddd;
      border-radius: 8px;
      background-color: #fff;
      position: relative;
      min-height: 150px;
      overflow: hidden;
      transition: all 0.3s ease;
    }


    .signature-display-container.clickable {
      cursor: pointer;
    }

    .signature-display-container.clickable:hover {
      border-color: #007bff;
      box-shadow: 0 0 8px rgba(0, 123, 255, 0.2);
    }

    .signature-display-container.has-signature {
      border-color: #28a745;
    }

    .signature-preview {
      width: 100%;
      height: 100%;
      position: relative;
      display: flex;
      flex-direction: column;
    }

    .signature-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      background-color: #fff;
    }

    .signature-info {
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
      color: white;
      padding: 8px 12px;
      font-size: 0.875rem;
    }

    .signer-name,
    .signed-time {
      display: flex;
      align-items: center;
      margin-bottom: 2px;
    }

    .signer-name i,
    .signed-time i {
      margin-right: 6px;
      width: 12px;
    }

    .signature-overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background-color: rgba(0, 123, 255, 0.1);
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      opacity: 0;
      transition: opacity 0.3s ease;
      color: #007bff;
      font-weight: 600;
    }

    .signature-display-container.clickable:hover .signature-overlay {
      opacity: 1;
    }

    .signature-overlay i {
      font-size: 2rem;
      margin-bottom: 8px;
    }

    .signature-placeholder {
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      color: #6c757d;
      text-align: center;
    }

    .signature-placeholder i {
      font-size: 3rem;
      margin-bottom: 12px;
      opacity: 0.5;
    }

    .signature-placeholder p {
      margin: 0;
      font-size: 1.1rem;
      font-weight: 500;
    }

    .signature-actions {
      margin-top: 8px;
      text-align: right;
    }

    .btn {
      padding: 6px 12px;
      border-radius: 4px;
      font-size: 0.875rem;
      font-weight: 500;
      border: 1px solid;
      transition: all 0.2s ease;
    }

    .btn i {
      margin-right: 4px;
    }

    .btn-outline-danger {
      color: #dc3545;
      border-color: #dc3545;
      background-color: transparent;
    }

    .btn-outline-danger:hover {
      color: #fff;
      background-color: #dc3545;
    }

    @media (max-width: 768px) {
      .signature-display-container {
        min-height: 120px;
      }

      .signature-placeholder i {
        font-size: 2rem;
      }

      .signature-placeholder p {
        font-size: 1rem;
      }

      .signature-info {
        font-size: 0.75rem;
        padding: 6px 8px;
      }
    }
  `],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormSignatureComponent),
      multi: true
    }
  ]
})
export class FormSignatureComponent implements AfterViewInit, ControlValueAccessor, OnDestroy {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() width: number = 500; // 默認寬度
  @Input() height: number = 150; // 默認高度
  @Input() required: boolean = false; // 是否必填
  @Input() completeButtonText: string = ''; // 完成按鈕文字
  @Input() clearButtonText: string = ''; // 清除按鈕文字

  @ViewChild('signatureModal') signatureModalRef!: SignatureModalComponent;

  currentSignature: SignatureData | null = null;
  isDisabled: boolean = false;

  onChange: (value: any) => void = () => {};
  onTouched: () => void = () => {};

  constructor(
    private i18nService: I18nService,
    private authService: AuthService
  ) {}

  get hasSignature(): boolean {
    return this.currentSignature?.imageData ? true : false;
  }

  writeValue(value: any): void {
    console.log('=== FormSignatureComponent writeValue ===');
    console.log('收到的值:', value);
    
    if (value) {
      // 處理動態表單編輯模式的資料結構
      if (value.type === 'signature' && value.originalValue) {
        // 編輯模式：value.originalValue 包含實際的簽名資料
        this.currentSignature = value.originalValue;
      }
      // 處理其他可能的巢狀結構（相容舊版）
      else if (value.type === 'signature' && value.value) {
        if (value.value.originalValue) {
          this.currentSignature = value.value.originalValue;
        } else if (value.value.imageData) {
          this.currentSignature = value.value;
        } else {
          this.currentSignature = null;
        }
      }
      // 支援舊格式（純字串）
      else if (typeof value === 'string') {
        this.currentSignature = {
          imageData: value,
          signerInfo: undefined
        };
      }
      // 支援標準 SignatureData 格式
      else if (value.imageData) {
        this.currentSignature = value;
      }
      // 其他情況
      else {
        this.currentSignature = null;
      }
    } else {
      this.currentSignature = null;
    }
    
    console.log('處理後的 currentSignature:', this.currentSignature);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  ngAfterViewInit() {
    console.log('=== FormSignatureComponent ngAfterViewInit ===');
    console.log('元件已掛載，currentSignature:', this.currentSignature);
    console.log('onChange 函數:', this.onChange);
    console.log('signatureModalRef:', this.signatureModalRef);
    // 不需要初始化畫布，因為使用 modal
  }

  ngOnDestroy() {
    console.log('=== FormSignatureComponent ngOnDestroy ===');
    // 清理工作
  }

  openSignatureModal() {
    console.log('=== FormSignatureComponent openSignatureModal 被呼叫 ===');
    console.log('isDisabled:', this.isDisabled);

    if (this.isDisabled) {
      console.log('元件被停用，無法開啟簽名 modal');
      return;
    }

    if (this.signatureModalRef) {
      console.log('準備開啟簽名 modal');
      this.signatureModalRef.openModal();
    } else {
      console.log('錯誤：signatureModalRef 不存在');
    }
  }

  onSignatureSaved(signatureData: SignatureData) {
    console.log('FormSignatureComponent 收到簽名資料:', signatureData); // 除錯用
    console.log('FormSignatureComponent onChange 函數:', this.onChange); // 檢查 onChange 是否已註冊
    this.currentSignature = signatureData;
    this.onChange(signatureData);
    this.onTouched();
    console.log('FormSignatureComponent 已更新 currentSignature:', this.currentSignature); // 除錯用
    console.log('FormSignatureComponent 已呼叫 onChange，值為:', signatureData); // 確認 onChange 已呼叫
  }

  onModalClosed() {
    // Modal 關閉時的處理
  }

  clearSignature(event?: Event) {
    if (event) {
      event.stopPropagation(); // 阻止事件冒泡，避免觸發 openSignatureModal
    }

    this.currentSignature = null;
    this.onChange(null);
    this.onTouched();
  }

  formatSignedTime(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
      return this.i18nService.translate('common.time.just_now');
    } else if (diffMins < 60) {
      return this.i18nService.translate('common.time.minutes_ago', { minutes: diffMins });
    } else if (diffHours < 24) {
      return this.i18nService.translate('common.time.hours_ago', { hours: diffHours });
    } else if (diffDays < 7) {
      return this.i18nService.translate('common.time.days_ago', { days: diffDays });
    } else {
      return date.toLocaleDateString();
    }
  }
}
